import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Sprout, ClipboardList } from "lucide-react";
import { Button } from "../ui/button";
import { QUESTIONS, buildResponses } from "../../lib/questionnaire";

// step: -1 = intro, 0..N-1 = questions, N = completion
export const Questionnaire = ({ nickname, submitting, onComplete }) => {
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const total = QUESTIONS.length;

  const finish = () => onComplete(buildResponses(answers));

  if (step === -1) {
    return (
      <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-10 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-xl w-full rounded-3xl border border-slate-800 bg-slate-900/70 backdrop-blur p-8 text-center"
          data-testid="questionnaire-intro"
        >
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
            <ClipboardList className="w-7 h-7 text-emerald-400" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-50">Before We Begin</h1>
          <p className="text-slate-300 mt-4 leading-relaxed">
            Tell us a little about your financial habits and goals. There are no right or wrong
            answers — this will help personalize your Burgeon experience.
          </p>
          <Button
            data-testid="questionnaire-begin-btn"
            onClick={() => setStep(0)}
            className="mt-8 h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold rounded-xl shadow-lg shadow-emerald-500/25"
          >
            Get Started <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
          <div className="mt-4">
            <button
              data-testid="questionnaire-skip-btn"
              onClick={() => onComplete([])}
              className="text-sm text-slate-400 hover:text-emerald-400 underline underline-offset-4"
            >
              Skip for now — continue anyway
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (step === total) {
    return (
      <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-10 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-xl w-full rounded-3xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.07] to-transparent bg-slate-900/70 backdrop-blur p-8 text-center"
          data-testid="questionnaire-complete"
        >
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto mb-5">
            <Sprout className="w-7 h-7 text-emerald-400" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-50">You're Ready to Grow</h1>
          <p className="text-slate-300 mt-4 leading-relaxed">
            Thanks{nickname ? `, ${nickname}` : ""}! We'll use your answers to make your Burgeon
            experience more relevant to you. Your decisions during the game may show how your financial
            habits change when you're faced with real-world choices.
          </p>
          <Button
            data-testid="start-journey-btn"
            onClick={finish}
            disabled={submitting}
            className="mt-8 h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold rounded-xl shadow-lg shadow-emerald-500/25 disabled:opacity-60"
          >
            {submitting ? "Starting…" : "Start My Journey"} <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
        </motion.div>
      </div>
    );
  }

  const q = QUESTIONS[step];
  const selected = answers[q.id];
  const isSelected = (val) => selected === val;
  const setAnswer = (val) => setAnswers((a) => ({ ...a, [q.id]: val }));
  const answered = selected !== undefined && selected !== "";
  const progress = ((step + 1) / total) * 100;

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-10 flex items-center justify-center">
      <div className="max-w-xl w-full" data-testid="questionnaire-question">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400">
              Question {step + 1} of {total}
            </span>
            <span className="text-xs text-slate-500">{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={q.id}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50 leading-snug mb-6">
              {q.question}
            </h2>

            <div className="space-y-2.5">
              {q.type === "single" &&
                q.options.map((opt, i) => (
                  <button
                    key={opt}
                    data-testid={`option-${q.id}-${i}`}
                    onClick={() => setAnswer(opt)}
                    className={`w-full text-left flex items-center gap-3 rounded-2xl border px-4 py-3.5 transition-colors ${
                      isSelected(opt)
                        ? "border-emerald-500/60 bg-emerald-500/10 text-slate-50"
                        : "border-slate-700 bg-slate-900/60 text-slate-200 hover:border-slate-600 hover:bg-slate-800/60"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                        isSelected(opt) ? "border-emerald-400 bg-emerald-400" : "border-slate-600"
                      }`}
                    >
                      {isSelected(opt) && <Check className="w-3 h-3 text-slate-950" />}
                    </span>
                    <span className="text-[15px]">{opt}</span>
                  </button>
                ))}

              {q.type === "scale" && (
                <div className="grid grid-cols-1 gap-2.5">
                  {q.options.map((opt, i) => (
                    <button
                      key={opt.value}
                      data-testid={`option-${q.id}-${i}`}
                      onClick={() => setAnswer(opt.value)}
                      className={`w-full text-left flex items-center gap-3 rounded-2xl border px-4 py-3.5 transition-colors ${
                        isSelected(opt.value)
                          ? "border-emerald-500/60 bg-emerald-500/10 text-slate-50"
                          : "border-slate-700 bg-slate-900/60 text-slate-200 hover:border-slate-600 hover:bg-slate-800/60"
                      }`}
                    >
                      <span
                        className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 font-mono font-bold ${
                          isSelected(opt.value)
                            ? "border-emerald-400 bg-emerald-400 text-slate-950"
                            : "border-slate-600 text-slate-300"
                        }`}
                      >
                        {opt.value}
                      </span>
                      <span className="text-[15px]">{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-8">
          <Button
            data-testid="questionnaire-prev-btn"
            variant="outline"
            onClick={() => setStep((s) => s - 1)}
            className="h-11 px-5 border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Previous
          </Button>
          <Button
            data-testid="questionnaire-next-btn"
            onClick={() => setStep((s) => s + 1)}
            disabled={!answered}
            className="h-11 px-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold rounded-xl disabled:opacity-50"
          >
            {step === total - 1 ? "Finish" : "Next"} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
